import React from 'react';
import { Button, Col, Row, Space, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { useNavigation } from '@refinedev/core';
import type { ColumnsType } from 'antd/es/table';
import type { PagedListState } from '@/hooks/usePagedList';
import type { EarnLimitFlagItem, EarnLimitFlagsResponse } from '@/types';
import { ListBody, LoadMoreFooter } from './ListParts';

const { Text } = Typography;

// earn_limit_flags.route → the endpoint the client called.
const EARN_ROUTE_LABELS: Record<string, string> = {
  economy: '/economy/earn',
  progression: '/progression/earn',
  match_result: '/progression/match-result',
};

export type EarnFlagsSummary = Omit<EarnLimitFlagsResponse, 'flags'>;

export const EarnFlagsTab: React.FC<{
  state: PagedListState<EarnLimitFlagItem, EarnFlagsSummary>;
}> = ({ state }) => {
  const { show } = useNavigation();
  const summary = state.list?.summary;
  const columns: ColumnsType<EarnLimitFlagItem> = [
    {
      title: 'Player',
      dataIndex: 'userDisplayName',
      render: (name: string | null | undefined, record) => (
        <Button type="link" size="small" onClick={() => show('players', record.userId)}>
          {name || record.userId.slice(0, 8)}
        </Button>
      ),
    },
    { title: 'IST day', dataIndex: 'istDay', width: 110 },
    {
      title: 'Route',
      dataIndex: 'route',
      width: 110,
      render: (route: string) => <Tag>{EARN_ROUTE_LABELS[route] ?? route}</Tag>,
    },
    { title: 'Source', dataIndex: 'source', width: 110 },
    { title: 'Currency', dataIndex: 'currency', width: 110 },
    {
      title: 'Day total / limit',
      width: 150,
      sorter: (a, b) => a.dayTotal - a.dayLimit - (b.dayTotal - b.dayLimit),
      render: (_: unknown, r) => (
        <Tooltip title={r.limitBasis}>
          <Text type="danger">{r.dayTotal.toLocaleString()}</Text>
          {' / '}
          {r.dayLimit.toLocaleString()}
        </Tooltip>
      ),
    },
    { title: 'Grants', dataIndex: 'grantCount', width: 80 },
    {
      title: 'Last grant',
      dataIndex: 'lastAmount',
      width: 100,
      render: (n: number) => n.toLocaleString(),
    },
    {
      title: 'Client (headers)',
      width: 170,
      render: (_: unknown, r) =>
        [r.platform ?? 'no x-platform', r.appVersion, r.buildVersion].filter(Boolean).join(' · '),
    },
    {
      title: 'Last flagged',
      dataIndex: 'lastFlaggedAt',
      width: 160,
      render: (d: string) => new Date(d).toLocaleString(),
    },
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        The Unity client tells the server what it earned on POST /economy/earn (currency) and POST
        /progression/earn (XP). Every call is granted, since it can be a genuine reward. Afterwards
        the player&apos;s total for the IST day is compared with what the reward config allows for
        that source and currency (rewarded ads: 20 &times; 250 coins and 100 &times; 1 match token;
        match rewards: what the server itself granted for the day&apos;s matches; level-up: rewards
        of the levels reached that day; daily, stump: the config&apos;s maximum). One row per
        player, day, source and currency that went over. The platform and version headers are as the
        client sent them and can be faked. Route /progression/match-result appears only on staging
        rows from 17-18 Oct; the route is closed to clients since then.
      </Text>
      <ListBody state={state}>
        {summary && (
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col>
              <Statistic
                title="Flagged rows"
                value={summary.total}
                valueStyle={{ color: '#cf1322' }}
              />
            </Col>
            <Col>
              <Statistic title="Unique players" value={summary.uniqueUsers} />
            </Col>
          </Row>
        )}
        {summary && summary.byKey.length > 0 && (
          <Space wrap style={{ marginBottom: 12 }}>
            {summary.byKey.map((b) => (
              <Tag key={`${b.route}:${b.source}:${b.currency}`} color="volcano">
                {b.source} {b.currency}: {b.count.toLocaleString()} (
                {b.uniqueUsers.toLocaleString()} players)
              </Tag>
            ))}
          </Space>
        )}
        <Table<EarnLimitFlagItem>
          dataSource={state.list?.rows ?? []}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ x: 1200 }}
          columns={columns}
        />
        <LoadMoreFooter state={state} noun="rows" />
      </ListBody>
    </div>
  );
};
